import React, { useState } from 'react';
import { RECALL_AGENT } from '../../settings-recall-agent/mockData';
import type { BuilderStep, RecallDraft, StepChannel } from '../../recall-builder/types';
import { modesFor, reorderSteps, sortStepsByDay, stepIssues } from '../../recall-builder/derive';
import { BuilderButton, BuilderFooter, BuilderHeader, BuilderTopBar, BuilderPage, FixList } from './BuilderShell';
import { StepsColumn } from './StepsColumn';
import { StepEditor } from './StepEditor';

export interface MessagesViewProps {
  draft: RecallDraft;
  onChange: (next: RecallDraft) => void;
  onBack: () => void;
  onContinue: () => void;
  onSaveDraft: () => void;
  /**
   * Which step to open. A fix link on page 3 names a step — *Step 3 needs a
   * brief* — and used to land on step 1, because this page always opened the
   * first one (§4.2).
   */
  initialStepId?: string | undefined;
}

/**
 * Page 2 — Messages (spec 21 §3).
 *
 * **Steps and editor side by side**, not an accordion: a full step editor is six
 * sections, and inside an accordion the page becomes one very long column where
 * the shape of the recall disappears. Side by side, the shape stays visible while
 * one step is being edited.
 */
export function MessagesView({
  draft,
  onChange,
  onBack,
  onContinue,
  onSaveDraft,
  initialStepId,
}: MessagesViewProps) {
  const [selectedId, setSelectedId] = useState<string | undefined>(
    (initialStepId !== undefined && draft.steps.some((s) => s.id === initialStepId) ? initialStepId : undefined) ??
      draft.steps[0]?.id,
  );
  const [shiftNote, setShiftNote] = useState<string | null>(null);

  const issues = stepIssues(draft);
  const selectedIndex = draft.steps.findIndex((s) => s.id === selectedId);
  const selected = draft.steps[selectedIndex];

  const setSteps = (steps: BuilderStep[]) => { onChange({ ...draft, steps }); };

  const addStep = (channel: StepChannel) => {
    const lastDay = draft.steps[draft.steps.length - 1]?.day ?? -7;
    const modes = modesFor(channel);
    const step: BuilderStep = {
      id: `s${String(Date.now())}`,
      day: lastDay + 7,
      channel,
      ...(modes.length > 0 ? { mode: modes[0] } : {}),
    };
    setSteps([...draft.steps, step]);
    setSelectedId(step.id);
  };

  const removeStep = (id: string) => {
    const next = draft.steps.filter((s) => s.id !== id);
    setSteps(next);
    setSelectedId(next[0]?.id);
  };

  return (
    <BuilderPage>
      <BuilderHeader
        active="messages"
        titleLead="What do they"
        titleAccent="hear"
        sub="Each step is one message. Write it yourself, or give ELVA a short brief and let it write each one."
        reachable={['audience']}
        onGoTo={onBack}
      />

      <BuilderTopBar
        draft={draft}
        onName={(name) => { onChange({ ...draft, name }); }}
        onChangeGoal={onBack}
        onChangeAudience={onBack}
      />

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        <StepsColumn
          draft={draft}
          selectedId={selectedId}
          issues={issues}
          onSelect={setSelectedId}
          onAdd={addStep}
          onReorder={(from, to) => {
            const result = reorderSteps(draft.steps, from, to);
            setSteps(result.steps);
            // Days must stay in order. When there is no room, the ones after
            // shift by a day — and the page says so rather than moving dates
            // under somebody silently.
            setShiftNote(
              result.shifted ? 'Days must stay in order, so the later steps moved on by a day.' : null,
            );
          }}
          onOptions={(next) => { onChange({ ...draft, ...next }); }}
        />

        <div className="min-w-0 flex-1">
          {shiftNote !== null ? (
            <div className="mb-3 rounded-[10px] bg-[#FBF3E4] px-3.5 py-2.5 text-[12px] text-[var(--warn-amber)]">
              {shiftNote}
            </div>
          ) : null}
          {selected === undefined ? (
            <div className="rounded-[16px] border border-dashed border-[var(--border-default)] px-6 py-12 text-center text-[13px] text-[var(--text-muted)]">
              Add a step to start writing.
            </div>
          ) : (
            <StepEditor
              draft={draft}
              step={selected}
              index={selectedIndex}
              humanCallOpenDays={RECALL_AGENT.deadlineOpenDays}
              onChange={(next) => { setSteps(draft.steps.map((s) => (s.id === next.id ? next : s))); }}
              onDayCommitted={() => {
                // A day typed out of order re-sorts the list rather than leaving
                // the step last and rendering the gap as `+-18 days` (§2.5).
                const sorted = sortStepsByDay(draft.steps);
                if (sorted.some((s, i) => s.id !== draft.steps[i]?.id)) {
                  setSteps(sorted);
                  setShiftNote('Steps are kept in day order, so this one moved.');
                }
              }}
              onRemove={() => { removeStep(selected.id); }}
            />
          )}
        </div>
      </div>

      {/* Continue is enabled even with issues — it is Start on page 3 that is
          blocked, so somebody can see the test page before fixing everything. */}
      <BuilderFooter left={<FixList issues={issues} onGoToStep={setSelectedId} />}>
        <BuilderButton onClick={onBack}>Back</BuilderButton>
        <BuilderButton onClick={onSaveDraft}>Save as draft</BuilderButton>
        <BuilderButton kind="primary" onClick={onContinue}>
          Continue to test and start
        </BuilderButton>
      </BuilderFooter>
    </BuilderPage>
  );
}
